import {
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import ChatMessageRenderer from './ChatMessageRenderer';
import AIChatEmptyState from './AIChatEmptyState';
import AIChatFirstUseConsent from './AIChatFirstUseConsent';
import { hasAiChatConsent, saveAiChatConsent } from './aiChatConsent';
import { useAIChat } from './AIChatContext';
import SendIcon from '../../../assets/icons/aiChat/SendIcon';
import SparkleIcon from '../../../assets/icons/aiChat/SparkleIcon';
import ChatVisualization from './ChatVisualization';
import { userStore } from '../../../utilities/common/userStore';
import type { SelfData } from '../../../shared/constants/user';

const getMessageText = (message: { content?: string; parts?: any[] }) => {
  if (message.content) return message.content;
  return (message.parts ?? [])
    .filter((p: any) => p.type === 'text' && p.text)
    .map((p: any) => p.text as string)
    .join('\n');
};

const formatChartLabel = (label: string): string => {
  const match = label.match(
    /^(\d{4})-(\d{2})-(\d{2})\s+to\s+(\d{4})-(\d{2})-(\d{2})$/,
  );

  if (!match) return label;

  const [, startYear, startMonth, startDay, , endMonth, endDay] = match;

  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  if (startMonth === endMonth) {
    return `${Number(startDay)}–${Number(endDay)} ${
      months[Number(startMonth) - 1]
    } '${startYear.slice(2)}`;
  }

  return `${Number(startDay)} ${months[Number(startMonth) - 1]}–${Number(
    endDay,
  )} ${months[Number(endMonth) - 1]} '${startYear.slice(2)}`;
};

export default function AIChatPanel() {
  const {
    open,
    setOpen,
    prompt,
    setPrompt,
    messages,
    error,
    stop,
    handleSubmit,
    sendPrompt,
    isDashboardPage,
    isGenerating,
    streamingAssistantMessageId,
    conversationId,
    conversations,
    historyReady,
    scopeError,
    openConversation,
    deleteConversation,
    clearChatForCurrentOrg,
  } = useAIChat();

  const [showHistory, setShowHistory] = useState(true);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [assistantMenuOpen, setAssistantMenuOpen] = useState(false);
  const user = useSyncExternalStore(
    userStore.subscribe,
    userStore.get,
  ) as SelfData | null;
  const userId = user?.id ?? '';
  const [hasConsent, setHasConsent] = useState(() => hasAiChatConsent(userId));

  useEffect(() => {
    setHasConsent(hasAiChatConsent(userId));
  }, [userId]);

  const acceptConsent = () => {
    if (!userId) return;
    saveAiChatConsent(userId);
    setHasConsent(true);
  };

  const historyButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) {
      setShowHistory(true);
    }
  }, [open]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const historyGroups = [
    { label: 'Today', items: [] as typeof conversations },
    { label: 'Yesterday', items: [] as typeof conversations },
    { label: 'Earlier', items: [] as typeof conversations },
  ];

  [...conversations]
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    .forEach((conversation) => {
      const updatedAt = new Date(conversation.updatedAt).getTime();

      const groupIndex =
        updatedAt >= today.getTime()
          ? 0
          : updatedAt >= yesterday.getTime()
            ? 1
            : 2;

      historyGroups[groupIndex].items.push(conversation);
    });

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const lastScrolledUserMessageIdRef = useRef<string | null>(null);
  const lastScrollTime = useRef<number>(Date.now());

  const scrollUserMessageToTop = (
    messageId: string,
    behavior: 'smooth' | 'instant' = 'smooth',
  ) => {
    const container = messagesContainerRef.current;
    const messageEl = messageRefs.current.get(messageId);
    if (!container || !messageEl) return;

    const containerTop = container.getBoundingClientRect().top;
    const messageTop = messageEl.getBoundingClientRect().top;
    const targetScrollTop = container.scrollTop + (messageTop - containerTop);

    container.scrollTo({
      top: Math.max(0, targetScrollTop),
      behavior,
    });
  };

  useEffect(() => {
    const lastMessage = messages.at(-1);
    if (
      !lastMessage ||
      lastMessage.role !== 'user' ||
      lastMessage.id === lastScrolledUserMessageIdRef.current
    ) {
      return;
    }

    lastScrolledUserMessageIdRef.current = lastMessage.id;

    const frameId = requestAnimationFrame(() => {
      scrollUserMessageToTop(lastMessage.id);
    });

    return () => cancelAnimationFrame(frameId);
  }, [messages]);

  useEffect(() => {
    if (!open || !isDashboardPage) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open, isDashboardPage]);

  useEffect(() => {
    if (!open || !isDashboardPage) return;
    const lastUserMessage = [...messages]
      .reverse()
      .find((message) => message.role === 'user');

    if (lastUserMessage && !lastScrollTime.current) {
      scrollUserMessageToTop(lastUserMessage.id, 'instant');
      lastScrollTime.current = Date.now();
    }
  }, [open, isDashboardPage, messages]);

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      lastScrollTime.current = 0;
      event.preventDefault();
      void sendPrompt(prompt);
    }
  };

  if (!isDashboardPage || !open) return null;

  const hasMessages = messages.length > 0;
  const needsConsent = !hasConsent;

  return (
    <>
      <div
        onClick={() => setOpen(false)}
        className="fixed inset-0 z-[70] bg-black/40"
      />
      <aside
        className={`fixed top-0 right-0 z-[80] h-dvh max-h-dvh w-full overflow-hidden bg-white shadow-2xl ${
          needsConsent
            ? 'flex max-w-[800px] flex-col'
            : showHistory
              ? 'flex flex-col md:grid md:max-w-[800px] md:grid-cols-[260px_minmax(0,1fr)] md:grid-rows-[auto_auto_minmax(0,1fr)_auto]'
              : 'flex max-w-[800px] flex-col'
        }`}
      >
        <header
          className={`flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-taupe-200 px-4 py-4 ${
            !needsConsent && showHistory ? 'md:col-start-2 md:row-start-1' : ''
          }`}
        >
          <div className="flex items-center gap-2">
            <SparkleIcon className="h-5 w-5 text-violet-500" />
            <h3 className="text-black-900 text-base font-semibold">
              Intellect AI
            </h3>
          </div>

          {!needsConsent && !showHistory && (
            <button
              ref={historyButtonRef}
              type="button"
              onClick={() => setShowHistory(true)}
              className="text-black-700 rounded-md bg-purple-100 px-2 py-1.5 text-sm hover:bg-purple-200 md:hidden"
            >
              History
            </button>
          )}

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              className="text-black-500 flex h-8 w-8 items-center justify-center rounded-md hover:bg-taupe-100"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M1 1l12 12M13 1L1 13"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </header>

        {needsConsent ? (
          <AIChatFirstUseConsent onAccept={acceptConsent} />
        ) : (
          <>
        {showHistory && (
          <section
            id="ai-chat-history"
            aria-label="Previous conversations"
            className="min-h-0 flex-1 overflow-y-auto bg-white px-3 py-3 md:col-start-1 md:row-span-4 md:row-start-1 md:border-r md:border-taupe-200"
            onKeyDown={(event) => {
              if (
                event.key === 'Escape' &&
                !window.matchMedia('(min-width: 768px)').matches
              ) {
                event.preventDefault();
                setShowHistory(false);
              }
            }}
          >
            <div className="mb-3 flex items-center justify-between border-b border-taupe-200 px-2 pb-3">
              <h3 className="text-black-900 text-sm font-semibold">
                Chat History
              </h3>

              <button
                type="button"
                aria-label="Start new conversation"
                disabled={!historyReady || Boolean(scopeError)}
                onClick={() => {
                  clearChatForCurrentOrg();
                  setOpenMenuId(null);

                  if (!window.matchMedia('(min-width: 768px)').matches) {
                    setShowHistory(false);
                  }
                }}
                className="text-black-600 flex h-7 w-7 items-center justify-center rounded-md text-xl hover:bg-purple-100 disabled:opacity-50"
              >
                +
              </button>
            </div>

            <div className="relative mb-3 px-2">
              <button
                type="button"
                aria-expanded={assistantMenuOpen}
                aria-haspopup="listbox"
                onClick={() => setAssistantMenuOpen((current) => !current)}
                className="text-black-800 flex w-full items-center justify-between rounded-lg border border-purple-200 bg-purple-50 px-3 py-2 text-sm transition hover:bg-purple-100"
              >
                <span className="flex items-center gap-2">
                  <SparkleIcon className="h-4 w-4 text-violet-500" />
                  <span className="font-medium">Intellect AI</span>
                </span>

                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  aria-hidden="true"
                  className={`transition-transform ${
                    assistantMenuOpen ? 'rotate-180' : ''
                  }`}
                >
                  <path
                    d="M3 5l4 4 4-4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {assistantMenuOpen && (
                <div
                  role="listbox"
                  className="absolute top-full right-2 left-2 z-30 mt-1 rounded-lg border border-taupe-200 bg-white p-1 shadow-lg"
                >
                  <button
                    type="button"
                    role="option"
                    aria-selected="true"
                    onClick={() => setAssistantMenuOpen(false)}
                    className="text-black-800 flex w-full items-center gap-2 rounded-md bg-purple-50 px-3 py-2 text-left text-sm hover:bg-purple-100"
                  >
                    <SparkleIcon className="h-4 w-4 text-violet-500" />
                    <span className="font-medium">Intellect AI</span>
                  </button>
                </div>
              )}
            </div>

            {!historyReady && !scopeError && (
              <p className="text-black-500 px-2 py-3 text-sm">
                Loading conversations...
              </p>
            )}

            {historyReady && conversations.length === 0 && (
              <p className="text-black-500 px-2 py-3 text-sm">
                Your conversations will appear here.
              </p>
            )}

            {historyReady &&
              historyGroups.map((group) =>
                group.items.length > 0 ? (
                  <div key={group.label} className="mb-3 last:mb-0">
                    <h4 className="text-black-500 px-2 py-1 text-xs font-semibold tracking-wide uppercase">
                      {group.label}
                    </h4>

                    <ul className="space-y-1">
                      {group.items.map((conversation) => {
                        const active = conversation.id === conversationId;
                        const date = new Date(conversation.updatedAt);

                        return (
                          <li key={conversation.id} className="relative">
                            <div
                              className={`flex items-center rounded-lg ${
                                active
                                  ? 'bg-purple-100 text-violet-800'
                                  : 'text-black-800 hover:bg-taupe-100'
                              }`}
                            >
                              <button
                                type="button"
                                title={conversation.title}
                                onClick={() => {
                                  openConversation(conversation.id);
                                  setOpenMenuId(null);

                                  // On mobile, close Recents so the selected chat becomes visible.
                                  if (
                                    !window.matchMedia('(min-width: 768px)')
                                      .matches
                                  ) {
                                    setShowHistory(false);
                                    historyButtonRef.current?.focus();
                                  }
                                }}
                                className="min-w-0 flex-1 px-3 py-2 text-left"
                              >
                                <span className="block pr-1 text-sm leading-5 font-medium break-words whitespace-normal">
                                  {conversation.title}
                                </span>

                                <span className="text-black-500 mt-1 block text-xs">
                                  {Number.isNaN(date.getTime())
                                    ? 'Saved conversation'
                                    : date.toLocaleString(undefined, {
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })}
                                  {active ? ' · Current' : ''}
                                </span>
                              </button>

                              <button
                                type="button"
                                aria-label={`Options for ${conversation.title}`}
                                aria-expanded={openMenuId === conversation.id}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setOpenMenuId((current) =>
                                    current === conversation.id
                                      ? null
                                      : conversation.id,
                                  );
                                }}
                                className="text-black-500 mr-2 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-xl hover:bg-white/70"
                              >
                                ⋯
                              </button>
                            </div>

                            {openMenuId === conversation.id && (
                              <div className="absolute top-10 right-2 z-20 min-w-[120px] rounded-lg border border-taupe-200 bg-white p-1 shadow-lg">
                                <button
                                  type="button"
                                  onClick={() => {
                                    deleteConversation(conversation.id);
                                    setOpenMenuId(null);
                                  }}
                                  className="w-full rounded-md px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : null,
              )}
          </section>
        )}

        {scopeError && (
          <p
            role="alert"
            className={`shrink-0 px-4 py-2 text-sm text-red-600 ${
              showHistory ? 'hidden md:col-start-2 md:row-start-2 md:block' : ''
            }`}
          >
            {scopeError}
          </p>
        )}
        <div
          ref={messagesContainerRef}
          className={`min-h-0 flex-1 overscroll-contain scroll-smooth ${
            showHistory
              ? `hidden md:col-start-2 md:row-start-3 ${
                  hasMessages ? 'md:block' : 'md:flex'
                }`
              : ''
          } ${
            hasMessages
              ? 'space-y-3 overflow-y-auto p-4'
              : 'flex flex-col overflow-hidden'
          }`}
        >
          {!hasMessages ? (
            <AIChatEmptyState
              onSelectPrompt={sendPrompt}
              disabled={isGenerating || !historyReady || Boolean(scopeError)}
            />
          ) : (
            messages.map((message) => {
              const messageText = getMessageText(message);
              let normalizedMessageText = messageText.replace(
                /(\]\([^)]+\))([A-Z])/g,
                '$1\n\n$2',
              );
              const isUser = message.role === 'user';

              const visualizations = !isUser
                ? (message.visualizations ?? [])
                    .map((args) => {
                      if (!args?.type || !Array.isArray(args?.data))
                        return null;
                      const rows = args.data as Record<
                        string,
                        string | number
                      >[];
                      if (rows.length === 0) return null;
                      const keys = Object.keys(rows[0]);
                      const labelKey =
                        keys.find((k) => typeof rows[0][k] === 'string') ??
                        keys[0];
                      const valueKeys = keys.filter((k) => k !== labelKey);
                      if (args.type === 'table') {
                        return {
                          type: 'table' as const,
                          title: String(args.title ?? ''),
                          columns: keys,
                          data: rows,
                        };
                      }
                      return {
                        type: args.type as 'bar_chart' | 'line_chart',
                        title: String(args.title ?? ''),
                        labels: rows.map((r) =>
                          formatChartLabel(String(r[labelKey])),
                        ),
                        datasets: valueKeys.map((k) => ({
                          label: k,
                          data: rows.map((r) => Number(r[k]) || 0),
                        })),
                      };
                    })
                    .filter(Boolean)
                : [];

              return (
                <div
                  key={message.id}
                  ref={(element) => {
                    if (element) {
                      messageRefs.current.set(message.id, element);
                    } else {
                      messageRefs.current.delete(message.id);
                    }
                  }}
                  className={`max-w-[95%] scroll-mt-3 rounded-lg px-3 py-2 text-sm ${
                    isUser
                      ? 'ml-auto bg-indigo-600 text-white'
                      : 'text-black-800 bg-taupe-100'
                  }`}
                >
                  {isUser ? (
                    <p className="whitespace-pre-wrap">{messageText}</p>
                  ) : (
                    <>
                      {(() => {
                        const resourcesMatch = normalizedMessageText.match(
                          /([\s\S]*?)(###\s*Recommended resources[\s\S]*?)(?=\n\n[^#]|$)([\s\S]*)/i,
                        );
                        let mainText = normalizedMessageText;
                        let resourcesText: string | null = null;

                        if (resourcesMatch) {
                          mainText = (
                            resourcesMatch[1] + (resourcesMatch[3] ?? '')
                          ).trim();
                          resourcesText = resourcesMatch[2].trim();
                        }

                        // Strip image markdown the model generates since we render the real chart
                        mainText = mainText
                          .replace(/!\[.*?\]\(attachment:\/\/.*?\)/g, '')
                          .trim();

                        return (
                          <>
                            {isGenerating &&
                              message.id === streamingAssistantMessageId &&
                              !messageText.trim() && (
                                <div className="flex items-center gap-2 py-1 text-violet-500">
                                  <SparkleIcon className="h-5 w-5 animate-pulse" />
                                  <span className="text-black-600 text-sm">
                                    Thinking...
                                  </span>
                                </div>
                              )}
                            {/* 1. Chart first */}
                            {visualizations.map((viz: any, i: number) => (
                              <ChatVisualization key={i} visualization={viz} />
                            ))}

                            {/* 2. Response text */}
                            {mainText && (
                              <ChatMessageRenderer
                                content={mainText}
                                variant="assistant"
                                isStreaming={
                                  isGenerating &&
                                  message.id === streamingAssistantMessageId
                                }
                              />
                            )}

                            {/* 3. Recommended resources last */}
                            {resourcesText && (
                              <ChatMessageRenderer
                                content={resourcesText}
                                variant="assistant"
                                isStreaming={false}
                              />
                            )}
                          </>
                        );
                      })()}
                    </>
                  )}
                </div>
              );
            })
          )}
          {hasMessages && <div className="h-[80vh]" />}

          {error && (
            <p className="mx-4 mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
              {error.message || 'Unable to generate a response right now.'}
            </p>
          )}
        </div>
        <form
          onSubmit={handleSubmit}
          className={`shrink-0 border-t border-taupe-200 px-4 py-4 ${
            showHistory ? 'hidden md:col-start-2 md:row-start-4 md:block' : ''
          }`}
        >
          <div className="flex items-center gap-2">
            <div className="relative min-w-0 flex-1">
              <input
                type="text"
                value={prompt}
                onChange={(event) => setPrompt(event.target.value)}
                onKeyDown={handleInputKeyDown}
                disabled={isGenerating || !historyReady || Boolean(scopeError)}
                placeholder="Ask Intellect AI..."
                className="text-black-900 placeholder:text-black-400 w-full rounded-2xl border border-taupe-300 bg-white py-3 pr-10 pl-4 text-sm shadow-[0_2px_6px_rgba(99,102,241,0.35)] outline-none focus:border-violet-400 disabled:cursor-not-allowed disabled:opacity-60"
              />
            </div>
            {isGenerating ? (
              <button
                type="button"
                onClick={() => stop()}
                className="text-black-600 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-taupe-300 text-xs font-medium hover:bg-taupe-100"
                aria-label="Stop generating"
              >
                Stop
              </button>
            ) : (
              <button
                type="submit"
                disabled={
                  !prompt.trim() || !historyReady || Boolean(scopeError)
                }
                aria-label="Send message"
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-r from-violet-500 to-indigo-600 text-white shadow-[0_4px_12px_rgba(99,102,241,0.35)] transition hover:from-violet-600 hover:to-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <SendIcon className="h-[18px] w-[18px]" />
              </button>
            )}
          </div>
          <p className="text-black-500 mt-2 text-center text-xs">
            AI can make mistakes. Review important information.
          </p>
        </form>
          </>
        )}
      </aside>
    </>
  );
}
